import { CalendarIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { FilterChipButton } from "@packages/core-client/collection/FilterChip.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { PeriodSelection } from "#shared/periodSelection.ts";
import { Popover, PopoverContent } from "@/components/ui/popover.tsx";
import { Tab, TabList, TabPanel, Tabs } from "@/components/ui/tabs.tsx";

import { usePeriodLabel } from "../hooks/usePeriodLabel.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";
import { useFilterOptionsClient } from "@domains/reviews/client/api/filterOptionsQuery.ts";
import { describePeriod } from "./comparison/comparisonSeries.ts";
import { CycleFilterList } from "./CycleFilterList.tsx";
import { TimeRangeFilterPanel } from "./TimeRangeFilterPanel.tsx";

const CYCLE_TAB = "cycle";
const TIME_RANGE_TAB = "time-range";

function useSoleCycle(reviewCycleIds: string[], language: string) {
    const client = useFilterOptionsClient();
    const isSole = reviewCycleIds.length === 1;
    const options = client.searchReviewCycles.queryOptions({ input: { restrictToReviewCycleIds: reviewCycleIds } });

    const { data } = useQuery(
        createQueryOptions(options, {
            queryKey: [...options.queryKey, { language }],
            enabled: isSole,
            throwOnError: false
        })
    );

    return isSole ? data?.items[0] : undefined;
}

export interface PeriodFilterButtonProps {
    period: PeriodSelection;
    onChange: (period: PeriodSelection) => void;
}

export function PeriodFilterButton({ period, onChange }: PeriodFilterButtonProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("PeriodFilterButton", { i18n: i18nextInstance });
    const describePeriodLabel = usePeriodLabel();

    const reviewCycleIds = period.reviewCycleIds ?? [];
    const soleCycle = useSoleCycle(reviewCycleIds, i18nextInstance.language);

    const descriptor = describePeriod(period);
    const isActive = descriptor.kind !== "all";

    const soleCycleLabel = soleCycle ? soleCycle.label || t("untitled") : undefined;
    const value = descriptor.kind === "cycles" ? (soleCycleLabel ?? describePeriodLabel(descriptor)) : (describePeriodLabel(descriptor) ?? t("allCycles"));

    const resolvedTab = descriptor.kind === "cycles" || descriptor.kind === "all" ? CYCLE_TAB : TIME_RANGE_TAB;

    const handleCycleSelectionChange = useCallback((selectedIds: string[]) => onChange(selectedIds.length > 0 ? { reviewCycleIds: selectedIds } : {}), [onChange]);

    return (
        <Popover>
            <FilterChipButton isActive={isActive}>
                <CalendarIcon aria-hidden="true" />
                <span className="text-muted-fg">{t("label")}</span>
                <span className="max-w-40 truncate font-medium">{value}</span>
                <ChevronDownIcon aria-hidden="true" />
            </FilterChipButton>
            <PopoverContent placement="bottom start" aria-label={t("label")} className="bg-bg p-3 [&_[data-slot=popover-inner]]:overflow-x-hidden">
                <div className="w-72">
                    <Tabs defaultSelectedKey={resolvedTab}>
                        <TabList aria-label={t("modes")}>
                            <Tab id={CYCLE_TAB}>{t("byCycle")}</Tab>
                            <Tab id={TIME_RANGE_TAB}>{t("byTimeRange")}</Tab>
                        </TabList>
                        <TabPanel id={CYCLE_TAB}>
                            <CycleFilterList selectedIds={reviewCycleIds} isWholeHistory={descriptor.kind === "all"} onSelectionChange={handleCycleSelectionChange} />
                        </TabPanel>
                        <TabPanel id={TIME_RANGE_TAB}>
                            <TimeRangeFilterPanel period={period} onChange={onChange} />
                        </TabPanel>
                    </Tabs>
                </div>
            </PopoverContent>
        </Popover>
    );
}
